'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { IconClipboardText, IconEdit } from '@tabler/icons-react';
import {
  ClaimIntakeSubmissionSummary,
  UpdateClaimIntakeRequestBody,
  updateClaimIntakeRequestBody,
} from 'bff';
import { useTranslations } from 'i11n';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, useToast } from 'ui';
import { ClaimSummary, ContextAwareCombobox } from '..';
import { useApiError } from '../hooks/useApiError';

export interface ClaimIntakeOption {
  label: string;
  value: string;
}

export interface ClaimIntakeFetchers {
  listLocalities: (search: string) => Promise<ClaimIntakeOption[]>;
  listServices: (
    verticalId: string,
    search: string,
  ) => Promise<ClaimIntakeOption[]>;
  listGoods: (
    verticalId: string,
    search: string,
  ) => Promise<ClaimIntakeOption[]>;
}

export const buildIntakeLocalityLabel = (
  locality: ClaimIntakeSubmissionSummary['locality'],
): string =>
  locality
    ? [locality.name, locality.department?.name, locality.province?.name]
        .filter(Boolean)
        .join(', ')
    : '';

/**
 * Structured intake of the form that opened the claim (Gamma). Rendered as a
 * summary card and editable through its own endpoint, not the claim one.
 */
export const ClaimIntakeCard = ({
  intake,
  originOrganizationName,
  onSave,
  fetchers,
}: {
  intake: ClaimIntakeSubmissionSummary;
  // Deriving insurer: the claim's customer organization, always read-only.
  originOrganizationName?: string | null;
  // Without a save handler the card is read-only (provider-app, field adjuster).
  onSave?: (body: UpdateClaimIntakeRequestBody) => Promise<unknown>;
  fetchers?: ClaimIntakeFetchers;
}) => {
  const t = useTranslations();
  const { toast } = useToast();
  const { handleError } = useApiError();

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // `company_customer_id` is legacy and no longer editable, but the endpoint
  // replaces the whole block: it travels back untouched so it is not cleared.
  const defaultValues = useMemo(
    (): UpdateClaimIntakeRequestBody => ({
      company_customer_id: intake.company_customer?.id ?? null,
      service_id: intake.service?.id ?? null,
      good_id: intake.good?.id ?? null,
      locality_id: intake.locality?.id ?? null,
    }),
    [intake],
  );

  const form = useForm<UpdateClaimIntakeRequestBody>({
    resolver: zodResolver(updateClaimIntakeRequestBody),
    defaultValues,
  });

  // Services and goods are scoped to the submission's vertical so the pair can
  // never end up split across two verticals.
  const verticalId = intake.vertical?.id;
  const canEdit = !!onSave && !!fetchers;

  const startEditing = useCallback(() => {
    form.reset(defaultValues);
    setIsEditing(true);
  }, [form, defaultValues]);

  const cancelEditing = useCallback(() => {
    form.reset(defaultValues);
    setIsEditing(false);
  }, [form, defaultValues]);

  const handleSave = useCallback(() => {
    if (isLoading) return;

    setIsLoading(true);

    onSave!(form.getValues())
      .then(() => {
        toast({
          title: t('claim.summary.intake.saved'),
        });
        setIsEditing(false);
      })
      .catch(handleError)
      .finally(() => setIsLoading(false));
  }, [isLoading, onSave, form, toast, t, handleError]);

  const emptyLabel = t('claim.summary.intake.empty');

  const renderField = useCallback(
    ({
      label,
      value,
      fieldName,
      defaultOption,
      loadOptions,
    }: {
      label: string;
      value: string;
      fieldName: keyof UpdateClaimIntakeRequestBody;
      defaultOption?: ClaimIntakeOption;
      loadOptions?: (search: string) => Promise<ClaimIntakeOption[]>;
    }) => (
      <ClaimSummary.CardAttribute>
        <ClaimSummary.CardLabel>{label}</ClaimSummary.CardLabel>
        {isEditing && loadOptions ? (
          <ContextAwareCombobox
            defaultOptions={[]}
            prefetch
            value={form.watch(fieldName) ?? undefined}
            defaultValue={defaultOption}
            placeholder={label}
            emptyStateMessage='No se encontraron resultados'
            classNames={{ button: 'h-8 px-3 text-sm truncate' }}
            loadOptions={loadOptions}
            onSelect={(selected) =>
              form.setValue(fieldName, selected?.value || null)
            }
            render={({ label: optionLabel }) => <p>{optionLabel}</p>}
          />
        ) : (
          <ClaimSummary.CardValue>{value || emptyLabel}</ClaimSummary.CardValue>
        )}
      </ClaimSummary.CardAttribute>
    ),
    [isEditing, form, emptyLabel],
  );

  const localityLabel = buildIntakeLocalityLabel(intake.locality);

  return (
    <ClaimSummary.CardGroup>
      <ClaimSummary.Header>
        <ClaimSummary.CardGroupLabel>
          <ClaimSummary.Icon>
            <IconClipboardText />
          </ClaimSummary.Icon>
          {t('claim.summary.intake.title')}
        </ClaimSummary.CardGroupLabel>

        {canEdit &&
          (isEditing ? (
            <div className='flex flex-row items-center space-x-2'>
              <Button
                onClick={cancelEditing}
                disabled={isLoading}
                variant='outline'
                size='sm'
              >
                Cancelar
              </Button>
              <Button loading={isLoading} onClick={handleSave} size='sm'>
                Guardar
              </Button>
            </div>
          ) : (
            <Button size='sm' variant='ghost' onClick={startEditing}>
              <IconEdit className='w-4 h-4' />
            </Button>
          ))}
      </ClaimSummary.Header>

      <ClaimSummary.CardGroupSeparator />

      <ClaimSummary.CardGroupFields>
        <ClaimSummary.CardAttribute>
          <ClaimSummary.CardLabel>
            {t('claim.summary.intake.insurer')}
          </ClaimSummary.CardLabel>
          <ClaimSummary.CardValue>
            {originOrganizationName || emptyLabel}
          </ClaimSummary.CardValue>
        </ClaimSummary.CardAttribute>

        {renderField({
          label: t('claim.summary.intake.service'),
          value: intake.service?.name ?? '',
          fieldName: 'service_id',
          defaultOption: intake.service
            ? { label: intake.service.name, value: intake.service.id }
            : undefined,
          loadOptions:
            fetchers && verticalId
              ? (search) => fetchers.listServices(verticalId, search)
              : undefined,
        })}

        {renderField({
          label: t('claim.summary.intake.good'),
          value: intake.good?.name ?? '',
          fieldName: 'good_id',
          defaultOption: intake.good
            ? { label: intake.good.name, value: intake.good.id }
            : undefined,
          loadOptions:
            fetchers && verticalId
              ? (search) => fetchers.listGoods(verticalId, search)
              : undefined,
        })}

        {renderField({
          label: t('claim.summary.intake.repair_locality'),
          value: localityLabel,
          fieldName: 'locality_id',
          defaultOption: intake.locality
            ? { label: localityLabel, value: intake.locality.id }
            : undefined,
          loadOptions: fetchers?.listLocalities,
        })}
      </ClaimSummary.CardGroupFields>
    </ClaimSummary.CardGroup>
  );
};
